import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { Mail, Waves } from "lucide-react";
import { supabase } from "../supabaseClient";
import { getAuthRedirectUrl } from "../utils/authRedirect";
import { LegalTextLinks } from "../components/LegalDocumentModal";

function getDefaultPath() {
    return Capacitor.isNativePlatform() ? "/home" : "/";
}

function safeNextPath(rawNext) {
    if (!rawNext) return getDefaultPath();
    if (typeof rawNext === "string" && rawNext.startsWith("/")) return rawNext;
    return getDefaultPath();
}

export default function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const isNative = Capacitor.isNativePlatform();

    const params = new URLSearchParams(location.search);
    const next = safeNextPath(location.state?.from || params.get("next"));

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState("");

    const canSubmit = useMemo(() => {
        return email.trim().length > 3 && password.length >= 6 && !busy;
    }, [email, password, busy]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setStatus("");
        setBusy(true);

        try {
            const { error } = await supabase.auth.signInWithPassword({
                email: email.trim().toLowerCase(),
                password,
            });

            if (error) {
                setStatus(error.message);
                return;
            }

            navigate(next, { replace: true });
        } catch (err) {
            console.error(err);
            setStatus("Login failed. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    const handleForgotPassword = async () => {
        setStatus("");
        const cleanEmail = email.trim().toLowerCase();

        if (!cleanEmail) {
            setStatus("Enter your email first, then tap Reset password.");
            return;
        }

        setBusy(true);
        try {
            const { error } = await supabase.auth.resetPasswordForEmail(cleanEmail, {
                redirectTo: getAuthRedirectUrl("/auth/update-password"),
            });

            if (error) {
                setStatus(error.message);
                return;
            }

            setStatus("Password reset link sent. Check your email.");
        } catch (err) {
            console.error(err);
            setStatus("Could not send reset email. Try again.");
        } finally {
            setBusy(false);
        }
    };

    const handleGoogleSignup = async () => {
        setStatus("");
        setBusy(true);

        try {
            const { error } = await supabase.auth.signInWithOAuth({
                provider: "google",
                options: {
                    redirectTo: getAuthRedirectUrl("/auth/callback"),
                    queryParams: {
                        prompt: "select_account",
                    },
                },
            });

            if (error) {
                setStatus(error.message);
            }
        } catch (err) {
            console.error(err);
            setStatus("Google signup could not be started. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <main className={`auth-shell auth-shell--coastal ${isNative ? "auth-shell--native" : "auth-shell--web"}`}>
            <section className="auth-coast-card auth-coast-card--login">
                <header className="auth-logo-header" aria-label="Beach Bum Pass">
                    <Link className="auth-logo-link" to={getDefaultPath()} aria-label="Beach Bum Pass home">
                        <img src="/assets/Graphics/Logo_Transparent.png" alt="" className="auth-logo-mark" />
                        <span className="auth-logo-text">Beach Bum Pass</span>
                    </Link>
                </header>

                <aside className="auth-brand-panel auth-brand-panel--simple" aria-label="Beach Bum Pass login brand panel">
                    <div className="auth-desktop-logo-lockup" aria-hidden="true">
                        <div className="auth-desktop-logo-orb">
                            <img src="/assets/Graphics/Logo_Transparent.png" alt="" className="auth-brand-logo" />
                        </div>
                        <div className="auth-desktop-brand-copy">
                            <span>Beach Bum Pass</span>
                            <small>Ready to scan</small>
                        </div>
                    </div>

                    <div className="auth-brand-copy">
                        <p className="auth-eyebrow">Welcome back</p>
                        <h1 className="auth-title auth-title--hero">Back to the beach.</h1>
                        <p className="auth-subtitle auth-subtitle--hero">
                            Pull up your pass, manage your crew, and keep your Emerald Coast plans ready to scan.
                        </p>
                    </div>

                </aside>

                <section className="auth-form-panel" aria-labelledby="login-title">
                    <div className="auth-form-heading">
                        <p className="auth-eyebrow">Member login</p>
                        <h2 id="login-title" className="auth-title">Log in to your pass.</h2>
                        <p className="auth-subtitle">Use the email you bought your pass with so we can find your trip fast.</p>
                    </div>

                    <form onSubmit={handleLogin} className="auth-form">
                        {!isNative ? (
                            <>
                                <button className="auth-primary" type="button" onClick={handleGoogleSignup} disabled={busy}>
                                    Login with Google
                                </button>
                                <div className="auth-divider" role="presentation">
                                    <span>or ride in with email</span>
                                </div>
                            </>
                        ) : null}

                        <label className="auth-label">
                            Email
                            <span className="auth-input-wrap">
                                <Mail size={18} aria-hidden="true" />
                                <input
                                    className="auth-input"
                                    type="email"
                                    placeholder="you@example.com"
                                    value={email}
                                    autoComplete="email"
                                    inputMode="email"
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </span>
                        </label>

                        <label className="auth-label">
                            Password
                            <input
                                className="auth-input"
                                type="password"
                                placeholder="Enter your password"
                                value={password}
                                autoComplete="current-password"
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                        </label>

                        <button className="auth-primary" type="submit" disabled={!canSubmit}>
                            {busy ? "Signing in…" : "Log in"}
                        </button>

                        <div className="auth-row auth-row--split">
                            <button className="auth-linkbtn" type="button" onClick={handleForgotPassword} disabled={busy}>
                                Reset password
                            </button>

                            <Link className="auth-link" to={`/register?next=${encodeURIComponent(next)}`}>
                                Create account
                            </Link>
                        </div>

                        {status ? <div className="auth-msg" aria-live="polite">{status}</div> : null}
                    </form>

                    <p className="auth-legal">By continuing, you agree to Beach Bum Pass <LegalTextLinks />.</p>
                </section>
            </section>
        </main>
    );
}
